/**
 * Canonical category preference schemas (R7).
 *
 * Each category renders its OWN chip-based fields - no free-text inputs.
 * Hair has NO Face section. Makeup has NO Hair section. Each category is
 * scoped to fields that matter for that service.
 *
 * Backend mirror: backend collection `category_preference_schemas` (seeded
 * via /backend/seed_preference_schemas.py). The frontend can fall back to
 * this static map if the API is unavailable.
 *
 * Field types:
 *  - "single"  : pick one chip
 *  - "multi"   : pick multiple chips
 *
 * Display order: array order is preserved.
 */

export const CATEGORY_PREFERENCE_SCHEMAS = {
  hair: {
    label: "Hair",
    blocks: [
      {
        key: "hair_basics",
        label: "Hair",
        fields: [
          { id: "hair_type", label: "Hair Type", type: "single", options: ["Straight", "Wavy", "Curly", "Coily", "Locs"] },
          { id: "hair_texture", label: "Texture", type: "single", options: ["Fine", "Medium", "Thick", "Coarse"] },
          { id: "porosity", label: "Porosity", type: "single", options: ["Low", "Normal", "High"] },
          { id: "scalp", label: "Scalp", type: "single", options: ["Dry", "Normal", "Oily", "Sensitive", "Flaky"] },
          { id: "length", label: "Length", type: "single", options: ["Pixie", "Short", "Shoulder", "Mid-back", "Waist", "Past waist"] },
          { id: "density", label: "Density", type: "single", options: ["Low", "Medium", "High"] },
          { id: "color_history", label: "Color History", type: "multi", options: ["Virgin", "Color-treated", "Highlighted", "Bleached", "Box-dyed", "Henna"] },
          { id: "allergies", label: "Allergies", type: "multi", options: ["Sulfates", "Parabens", "Silicones", "Fragrance", "PPD (color dye)", "Latex"] },
        ],
      },
    ],
  },
  makeup: {
    label: "Makeup",
    blocks: [
      {
        key: "face_basics",
        label: "Face",
        fields: [
          { id: "skin_type", label: "Skin Type", type: "single", options: ["Dry", "Normal", "Combination", "Oily", "Sensitive"] },
          { id: "undertone", label: "Undertone", type: "single", options: ["Cool", "Neutral", "Warm", "Olive"] },
          { id: "foundation_range", label: "Foundation Range", type: "single", options: ["Fair", "Light", "Light-medium", "Medium", "Medium-tan", "Tan", "Deep", "Rich"] },
          { id: "finish", label: "Finish", type: "single", options: ["Matte", "Natural", "Dewy", "Glow"] },
          { id: "coverage", label: "Coverage", type: "single", options: ["Sheer", "Light", "Medium", "Full"] },
          { id: "skin_concerns", label: "Skin Concerns", type: "multi", options: ["Acne", "Hyperpigmentation", "Redness", "Texture", "Fine lines", "Dryness", "Dark circles"] },
        ],
      },
      {
        key: "eyes_lips",
        label: "Eyes & Lips",
        fields: [
          { id: "eye_shape", label: "Eye Shape", type: "single", options: ["Almond", "Round", "Hooded", "Monolid", "Downturned", "Upturned"] },
          { id: "lash_style", label: "Lash Style", type: "single", options: ["Natural", "Wispy", "Cat-eye", "Doll", "Dramatic", "Strip", "Cluster"] },
          { id: "lip_shape", label: "Lip Shape", type: "single", options: ["Full", "Thin", "Heart", "Wide", "Asymmetrical"] },
          { id: "lip_finish", label: "Lip Finish", type: "single", options: ["Matte", "Satin", "Gloss", "Stain", "Nude"] },
        ],
      },
      {
        key: "allergies",
        label: "Allergies",
        fields: [
          { id: "allergies", label: "Allergies / Avoid", type: "multi", options: ["Latex", "Fragrance", "Lanolin", "Talc", "Coconut", "Glycerin", "Salicylates"] },
        ],
      },
    ],
  },
  nails: {
    label: "Nails",
    blocks: [
      {
        key: "nail_basics",
        label: "Nails",
        fields: [
          { id: "nail_state", label: "Current State", type: "single", options: ["Natural", "Bitten", "Brittle", "Strong", "Polished"] },
          { id: "nail_length", label: "Length", type: "single", options: ["Short", "Medium", "Long", "Extra long"] },
          { id: "nail_shape", label: "Shape", type: "single", options: ["Square", "Round", "Oval", "Almond", "Stiletto", "Coffin", "Squoval"] },
          { id: "nail_service", label: "Service", type: "single", options: ["Polish", "Gel", "Acrylic", "Dip", "Builder gel", "Press-on"] },
          { id: "design_pref", label: "Design Style", type: "multi", options: ["Minimal", "French", "Classic", "Glitter", "Chrome", "3D", "Hand-painted"] },
          { id: "allergies", label: "Allergies", type: "multi", options: ["HEMA", "Acrylates", "Formaldehyde", "Toluene", "Latex"] },
        ],
      },
    ],
  },
  lashes: {
    label: "Lashes",
    blocks: [
      {
        key: "lash_basics",
        label: "Lashes",
        fields: [
          { id: "natural_density", label: "Natural Density", type: "single", options: ["Sparse", "Medium", "Dense"] },
          { id: "eye_shape", label: "Eye Shape", type: "single", options: ["Almond", "Round", "Hooded", "Monolid", "Downturned", "Upturned"] },
          { id: "lash_style", label: "Style", type: "single", options: ["Classic", "Hybrid", "Volume", "Mega volume", "Wet look"] },
          { id: "curl", label: "Curl", type: "single", options: ["B", "C", "CC", "D", "DD", "L", "M"] },
          { id: "length_pref", label: "Length", type: "single", options: ["Natural (8-11mm)", "Medium (12-14mm)", "Long (15-17mm)", "Dramatic (18mm+)"] },
          { id: "allergies", label: "Allergies", type: "multi", options: ["Cyanoacrylate (glue)", "Latex", "Formaldehyde", "Carbon black"] },
          { id: "comfort", label: "Comfort", type: "multi", options: ["Sensitive eyes", "Watery eyes", "Contact lenses"] },
        ],
      },
    ],
  },
  culinary: {
    label: "Culinary",
    blocks: [
      {
        key: "diet",
        label: "Dietary",
        fields: [
          { id: "diet", label: "Diet", type: "multi", options: ["Vegetarian", "Vegan", "Pescatarian", "Halal", "Kosher", "Gluten-free", "Dairy-free", "Keto", "Paleo"] },
          { id: "allergies", label: "Allergies", type: "multi", options: ["Peanut", "Tree nut", "Shellfish", "Egg", "Dairy", "Gluten", "Soy", "Sesame", "Fish"] },
          { id: "spice_level", label: "Spice Level", type: "single", options: ["Mild", "Medium", "Spicy", "Very spicy"] },
          { id: "dish_pref", label: "Dish Type", type: "multi", options: ["Savory", "Sweet", "Comfort", "Light", "Plated", "Family-style", "Tasting menu"] },
          { id: "dessert_pref", label: "Dessert", type: "multi", options: ["Cake", "Pie", "Cookies", "Cupcakes", "Frozen", "Pastry", "No dessert"] },
        ],
      },
    ],
  },
  events: {
    label: "Events",
    blocks: [
      {
        key: "events_basics",
        label: "Event",
        fields: [
          { id: "occasion_type", label: "Occasion", type: "single", options: ["Birthday", "Wedding", "Bridal", "Anniversary", "Corporate", "Brand launch", "Photoshoot", "Other"] },
          { id: "vibe", label: "Vibe", type: "multi", options: ["Intimate", "Glam", "Casual", "Editorial", "Playful", "Elegant", "Edgy"] },
          { id: "color_palette", label: "Color Palette", type: "multi", options: ["Neutral", "Pastel", "Bold", "Black tie", "Metallic", "Earth tones", "Jewel tones"] },
          { id: "guest_count", label: "Guest Count", type: "single", options: ["Just me", "2-5", "6-15", "16-50", "50+"] },
        ],
      },
    ],
  },
  instructors: {
    label: "Instructors",
    blocks: [
      {
        key: "learning",
        label: "Learning",
        fields: [
          { id: "skill_level", label: "Skill Level", type: "single", options: ["Beginner", "Intermediate", "Advanced", "Pro"] },
          { id: "format_pref", label: "Format", type: "multi", options: ["1:1", "Small group", "Workshop", "Masterclass", "Online", "In-person"] },
          { id: "focus_areas", label: "Focus Areas", type: "multi", options: ["Technique", "Business", "Branding", "Photography", "Client mgmt", "Pricing"] },
        ],
      },
    ],
  },
  wellness: {
    label: "Wellness",
    blocks: [
      {
        key: "wellness_basics",
        label: "Wellness",
        fields: [
          { id: "modality", label: "Modality", type: "multi", options: ["Massage", "Yoga", "Meditation", "Reiki", "Acupuncture", "Sound bath", "Breathwork"] },
          { id: "pressure", label: "Pressure", type: "single", options: ["Light", "Medium", "Firm", "Deep tissue"] },
          { id: "focus_areas", label: "Focus Areas", type: "multi", options: ["Neck/shoulders", "Lower back", "Hips", "Legs", "Feet", "Full body", "Stress"] },
          { id: "limitations", label: "Physical Limitations", type: "multi", options: ["Pregnancy", "Recent surgery", "Chronic pain", "Mobility", "Hypertension", "None"] },
          { id: "allergies", label: "Sensitivities", type: "multi", options: ["Essential oils", "Fragrance", "Latex", "Heat"] },
        ],
      },
    ],
  },
  travel: {
    label: "Travel",
    blocks: [
      {
        key: "travel_basics",
        label: "Travel",
        fields: [
          { id: "trip_type", label: "Trip Type", type: "multi", options: ["Beach", "City", "Adventure", "Wellness", "Cultural", "Foodie", "Romantic", "Family"] },
          { id: "pace", label: "Pace", type: "single", options: ["Slow", "Balanced", "Packed"] },
          { id: "lodging", label: "Lodging", type: "multi", options: ["Boutique hotel", "Resort", "Villa", "Airbnb", "Hostel"] },
          { id: "diet", label: "Dietary", type: "multi", options: ["Vegetarian", "Vegan", "Halal", "Kosher", "Gluten-free", "Dairy-free", "Allergies"] },
        ],
      },
    ],
  },
  dispensary: {
    label: "Dispensary",
    blocks: [
      {
        key: "dispensary_basics",
        label: "Preferences",
        fields: [
          { id: "experience", label: "Experience Level", type: "single", options: ["New", "Casual", "Regular", "Connoisseur"] },
          { id: "format_pref", label: "Format", type: "multi", options: ["Flower", "Pre-roll", "Vape", "Edible", "Tincture", "Topical"] },
          { id: "effect_pref", label: "Desired Effect", type: "multi", options: ["Relax", "Sleep", "Energy", "Focus", "Creative", "Pain relief", "Social"] },
          { id: "potency", label: "Potency", type: "single", options: ["Microdose", "Light", "Moderate", "Strong"] },
        ],
      },
    ],
  },
  retail: {
    label: "Retail",
    blocks: [
      {
        key: "retail_basics",
        label: "Style",
        fields: [
          { id: "size_top", label: "Top Size", type: "single", options: ["XS", "S", "M", "L", "XL", "XXL", "1X", "2X", "3X"] },
          { id: "size_bottom", label: "Bottom Size", type: "single", options: ["00", "0", "2", "4", "6", "8", "10", "12", "14", "16", "18", "20"] },
          { id: "size_shoe", label: "Shoe Size", type: "single", options: ["5", "6", "7", "8", "9", "10", "11", "12"] },
          { id: "fit_pref", label: "Fit", type: "multi", options: ["Tailored", "Relaxed", "Oversized", "Cropped", "Body-con"] },
          { id: "vibe", label: "Style Vibe", type: "multi", options: ["Minimal", "Streetwear", "Classic", "Edgy", "Boho", "Glam", "Athleisure"] },
          { id: "palette", label: "Palette", type: "multi", options: ["Neutrals", "Earth", "Pastels", "Brights", "Black & white", "Metallics"] },
        ],
      },
    ],
  },
};

const ALIASES = { nail: "nails", lash: "lashes", event: "events", instructor: "instructors" };

/**
 * Resolve a category preference schema by key.
 * @param {string} key - "hair", "makeup", "nail" (alias for nails), etc.
 * @returns {object|null} { label, blocks: [...] } or null if unknown
 */
export function getPreferenceSchema(key) {
  if (!key) return null;
  const k = String(key).toLowerCase().replace(/^cat_/, "").trim();
  return CATEGORY_PREFERENCE_SCHEMAS[k] || CATEGORY_PREFERENCE_SCHEMAS[ALIASES[k]] || null;
}
